import { useEffect, useMemo, useState } from "react";
import { Archive, Search } from "lucide-react";
import NewsFeed from "../components/NewsFeed";
import NewsArchiveFeed from "../components/NewsArchiveFeed";
import { PageHeader } from "../components/common";
import { SITE } from "../lib/site";

/* Travel News hub — TravelPulse-style category taxonomy powered by Google News RSS.
   Stories always link out to the original source (TravelPulse, Reuters, Skift, …). */

const CATEGORIES = [
  ["travel", "Top Stories"],
  ["destinations", "Destinations"],
  ["aviation", "Airlines & Airports"],
  ["hotels", "Hotels & Resorts"],
  ["cruise", "Cruise"],
  ["impacting", "Impacting Travel"],
  ["deals", "Deals"],
];

const REGIONS = [
  ["", "Worldwide"],
  ["usa", "USA"],
  ["europe", "Europe"],
  ["asia", "Asia"],
  ["india", "India"],
];

export default function TravelNews() {
  const [cat, setCat] = useState("travel");
  const [region, setRegion] = useState("");
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");

  useEffect(() => {
    document.title = `Travel News — Destinations, Airlines, Hotels, Cruise & Deals | ${SITE.name}`;
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 500);
    return () => clearTimeout(t);
  }, [q]);

  const path = useMemo(() => {
    const p = new URLSearchParams({ category: cat, limit: "24" });
    if (region) p.set("region", region);
    if (debouncedQ) p.set("q", debouncedQ);
    return `/news?${p.toString()}`;
  }, [cat, region, debouncedQ]);

  return (
    <div data-testid="travel-news-page">
      <PageHeader eyebrow={`${SITE.name} · Newsroom`} title="Travel News"
        subtitle="The latest travel headlines from trusted sources worldwide — destinations, airlines & airports, hotels, cruise lines, disruptions and deals. Updated every 30 minutes." />
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-10">
        {/* category tabs */}
        <div className="flex flex-wrap gap-2 mb-6" data-testid="news-category-tabs">
          {CATEGORIES.map(([id, label]) => (
            <button key={id} onClick={() => setCat(id)} data-testid={`news-tab-${id}`}
              className={`px-4 py-2 text-sm font-700 border transition-colors ${cat === id
                ? "bg-blue-700 border-blue-700 text-white"
                : "bg-white border-zinc-300 text-zinc-700 hover:border-blue-600"}`}>
              {label}
            </button>
          ))}
          <button onClick={() => setCat("archive")} data-testid="news-tab-archive"
            className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-700 border transition-colors ${cat === "archive"
              ? "bg-zinc-800 border-zinc-800 text-white"
              : "bg-white border-zinc-300 text-zinc-700 hover:border-zinc-700"}`}>
            <Archive className="w-3.5 h-3.5" /> Archive
          </button>
        </div>

        {/* region + search filters */}
        <div className="flex flex-wrap items-center gap-3 mb-8">
          <div className="relative flex-1 min-w-[220px] max-w-xl">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="news-search-input"
              placeholder="Search a destination, airline, hotel brand…"
              className="w-full border border-zinc-300 bg-white pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-blue-600" />
          </div>
          <select value={region} onChange={(e) => setRegion(e.target.value)} data-testid="news-region-select"
            disabled={cat === "archive"}
            className="border border-zinc-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:border-blue-600 disabled:opacity-50">
            {REGIONS.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
          </select>
        </div>

        {cat === "archive" ? <NewsArchiveFeed q={debouncedQ} /> : <NewsFeed path={path} />}

        <p className="mt-8 text-xs text-muted-foreground" data-testid="news-attribution">
          Headlines aggregated via Google News. All stories open at the original publisher's site.
          Every headline is kept in our archive for later reference; links that stop working are removed automatically.
        </p>
      </div>
    </div>
  );
}
